/**
 * Puces NFC du restaurant : générer l'adresse à programmer sur une puce, la copier,
 * désactiver une puce perdue ou copiée.
 *
 * Seul format accepté par le serveur : {TAG_BASE_URL}/t?n=…&s=… (puce signée, nfcTokens).
 * L'adresse est fabriquée par la Cloud Function (generateNfcBatch) : rien n'est généré
 * dans le navigateur. Elle est propre au projet (preprod / prod) et ne change plus :
 * une puce programmée reste valable tant qu'elle n'est pas désactivée (1 an).
 *
 * Programmer une puce : application « NFC Tools » (iOS / Android) → Écrire → Ajouter un
 * enregistrement → URL / URI → coller l'adresse → Écrire, puis approcher la puce (NTAG213
 * ou plus). Verrouiller la puce en écriture une fois testée.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { firestoreService } from '../../shared/services/firestoreService';
import type { NfcTagInfo } from '../../shared/services/firestoreService';
import { CheckIcon, WarningIcon } from './icons/UiIcons';

interface NfcTagsManagerProps {
    restaurantId: string;
}

const formatDate = (ms: number | null) =>
    ms ? new Date(ms).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const NfcTagsManager: React.FC<NfcTagsManagerProps> = ({ restaurantId }) => {
    const [tags, setTags] = useState<NfcTagInfo[] | null>(null);
    const [label, setLabel] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

    const load = useCallback(async () => {
        setError('');
        try {
            setTags(await firestoreService.getNfcTokens(restaurantId));
        } catch (e: any) {
            setTags([]);
            setError(e?.message ?? 'Impossible de charger les puces.');
        }
    }, [restaurantId]);

    useEffect(() => { if (restaurantId) load(); }, [restaurantId, load]);

    const generate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setError('');
        setNotice('');
        try {
            const { tokens } = await firestoreService.generateNfcBatch(1, restaurantId, label.trim() || undefined);
            setNotice(`Puce « ${tokens[0]?.label || 'sans nom'} » créée : copiez son adresse et programmez-la.`);
            setLabel('');
            await load();
        } catch (err: any) {
            setError(err?.message ?? 'Création impossible.');
        } finally {
            setBusy(false);
        }
    };

    const copy = async (url: string) => {
        try {
            await navigator.clipboard.writeText(url);
            setNotice('Adresse copiée.');
        } catch {
            setNotice('Copie impossible : sélectionnez l\'adresse et copiez-la.');
        }
    };

    const revoke = async (nonce: string) => {
        setBusy(true);
        setError('');
        try {
            await firestoreService.revokeNfcToken(nonce, restaurantId);
            setNotice('Puce désactivée : elle ne valide plus aucun repas.');
            setConfirmRevoke(null);
            await load();
        } catch (err: any) {
            setError(err?.message ?? 'Désactivation impossible.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            <div>
                <h1 className="page-title">Puces NFC</h1>
                <p className="page-subtitle">
                    Chaque puce porte une adresse signée, propre à votre restaurant. Le client l'approche de son
                    téléphone pour valider son repas (une fois par jour). Sans NFC, scannez son QR depuis
                    « Encaissement fidélité ».
                </p>
            </div>

            <form onSubmit={generate} className="bg-white rounded-3xl border border-black/[0.08] p-5 flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="flex-1">
                    <label htmlFor="nfc-tag-label" className="block text-sm font-medium text-gray-700 mb-1.5">Emplacement de la puce</label>
                    <input
                        id="nfc-tag-label"
                        type="text"
                        maxLength={40}
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="Ex. Comptoir, Table 4, Terrasse"
                        className="w-full h-10 border border-gray-400 rounded-lg px-3.5 text-[15px] hover:border-gray-500 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"
                    />
                </div>
                <button
                    type="submit"
                    disabled={busy}
                    className="h-10 px-4 bg-brand-500 text-cream text-sm font-medium rounded-lg hover:bg-brand-600 disabled:opacity-50"
                >
                    {busy ? 'Patientez…' : 'Créer une puce'}
                </button>
            </form>

            <div aria-live="polite">
                {notice && (
                    <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl p-3 inline-flex items-start gap-2">
                        <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0" /> {notice}
                    </p>
                )}
                {error && (
                    <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3 inline-flex items-start gap-2">
                        <WarningIcon className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
                    </p>
                )}
            </div>

            <details className="bg-cream-200 rounded-2xl p-4 text-sm text-gray-600">
                <summary className="font-semibold text-gray-800 cursor-pointer">Programmer une puce (2 minutes)</summary>
                <ol className="list-decimal pl-5 mt-3 space-y-1">
                    <li>Installez l'application gratuite « NFC Tools » sur un téléphone avec NFC.</li>
                    <li>Écrire → Ajouter un enregistrement → URL / URI → collez l'adresse de la puce.</li>
                    <li>Appuyez sur « Écrire », puis approchez la puce (NTAG213 ou plus).</li>
                    <li>Testez avec l'application ATABLE, puis verrouillez la puce en écriture (« Autres » → « Verrouiller »).</li>
                </ol>
            </details>

            <div className="bg-white rounded-3xl border border-black/[0.08] divide-y divide-black/[0.06]">
                {tags === null && <p className="p-5 text-sm text-gray-400">Chargement…</p>}
                {tags !== null && tags.length === 0 && !error && (
                    <p className="p-5 text-sm text-gray-500">Aucune puce pour l'instant : créez-en une ci-dessus.</p>
                )}
                {tags?.map((t) => (
                    <div key={t.nonce} className={`p-5 space-y-2 ${t.consumed ? 'opacity-60' : ''}`}>
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="font-bold text-gray-900">{t.label || 'Puce sans nom'}</p>
                                <p className="text-xs text-gray-500">
                                    Créée le {formatDate(t.createdAt)} · {t.usageCount} validation{t.usageCount > 1 ? 's' : ''}
                                    {t.lastUsedAt ? ` · dernière le ${formatDate(t.lastUsedAt)}` : ''}
                                </p>
                            </div>
                            {t.consumed ? (
                                <span className="text-xs font-bold text-gray-500 bg-gray-100 rounded-full px-3 py-1">Désactivée</span>
                            ) : confirmRevoke === t.nonce ? (
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setConfirmRevoke(null)} className="text-xs font-bold text-gray-600 px-3 py-1.5 rounded-lg bg-gray-100">
                                        Garder
                                    </button>
                                    <button type="button" disabled={busy} onClick={() => revoke(t.nonce)} className="text-xs font-bold text-white px-3 py-1.5 rounded-lg bg-red-600 disabled:opacity-50">
                                        Confirmer la désactivation
                                    </button>
                                </div>
                            ) : (
                                <button type="button" onClick={() => setConfirmRevoke(t.nonce)} className="text-xs font-bold text-red-600 hover:text-red-800">
                                    Désactiver
                                </button>
                            )}
                        </div>
                        {!t.consumed && (
                            <div className="flex gap-2 items-center">
                                <input
                                    readOnly
                                    value={t.url}
                                    aria-label={`Adresse à programmer sur la puce ${t.label || ''}`}
                                    onFocus={(e) => e.currentTarget.select()}
                                    className="flex-1 min-w-0 text-xs font-mono bg-gray-50 border border-gray-400 rounded-lg px-3 py-2 text-gray-700"
                                />
                                <button type="button" onClick={() => copy(t.url)} className="text-xs font-bold text-indigo-600 hover:text-indigo-800 whitespace-nowrap">
                                    Copier
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default NfcTagsManager;
